import React, { useEffect, useState } from 'react';

import { SourceBook } from "@dndModels";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

import MyTTRPGAPI from "@utilities/smart_api";
import { SourceBookContent } from "@utilities/smart_api_class";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FlexBox from '@components/layout/FlexBox';
import DandDButtonBox from './DandDButtonBoxSimple';

const api = MyTTRPGAPI.getInstance();

interface Props {
  source_book: SourceBook;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

const pluralFor = (content: SourceBookContent) =>
  DATA_TYPE_INFO.find(o => o.data_type === content.data_type)?.plural ?? content.data_type;

// Lists the contents, grouped by data type.
const ContentList = ({ contents }: { contents: SourceBookContent[] }) => {
  const groups: { plural: string, names: string[] }[] = [];
  for (const content of contents) {
    const plural = pluralFor(content);
    const group = groups.find(o => o.plural === plural);
    if (group) {
      group.names.push(content.name);
    } else {
      groups.push({ plural, names: [content.name] });
    }
  }
  return (
    <FlexBox direction="column" style={{ maxHeight: 250, overflowY: "auto" }}>
      { groups.map(group => (
        <span key={group.plural}>
          <b>{ group.plural } ({ group.names.length }):</b> { group.names.join(", ") }
        </span>
      ))}
    </FlexBox>
  );
}

// Confirmation for deleting a source book. Everything that's only in it gets deleted with it,
// and everything that's also in another source book is just taken out of it.
export default function SourceBookDeleteModal({
  source_book,
  open,
  onClose,
  onDeleted
}: Props) {
  const [contents, setContents] = useState<SourceBookContent[] | null>(null);
  const [progress, setProgress] = useState<{ done: number, total: number } | null>(null);
  const [failed, setFailed] = useState<string[]>([]);

  useEffect(() => {
    if (open && contents === null) {
      api.findSourceBookContents(source_book._id).then(setContents);
    }
  }, [open, contents, source_book]);

  const only_in = (contents ?? []).filter(o => o.source_books_old.every(sb => sb.id === source_book._id));
  const shared = (contents ?? []).filter(o => !o.source_books_old.every(sb => sb.id === source_book._id));
  const running = progress !== null;

  const deleteIt = async () => {
    if (!contents) {
      return;
    }
    setFailed([]);
    setProgress({ done: 0, total: contents.length });
    const new_failed = await api.deleteSourceBook(source_book, contents, (done, total) => setProgress({ done, total }));
    setProgress(null);
    if (new_failed.length > 0) {
      setFailed(new_failed);
      setContents(null); // Reload what's left.
    } else {
      onDeleted();
    }
  }

  return (
    <ModalOrDrawer open={open} onClose={() => { if (!running) { onClose(); } }}>
      <FlexBox direction="column" className="padded">
        <span className="header">Delete { source_book.name }?</span>
        { contents === null ?
          <span>Finding everything in { source_book.name }...</span>
        :
          <>
            { only_in.length > 0 ?
              <>
                <span style={{ color: "red" }}>
                  These { only_in.length } things are only in { source_book.name },
                  so they will be deleted as well when the source book is deleted:
                </span>
                <ContentList contents={only_in} />
              </>
            :
              <span>Nothing is only in { source_book.name }, so nothing else will be deleted.</span>
            }
            { shared.length > 0 &&
              <>
                <span>
                  These { shared.length } things are also in other source books.
                  They won&apos;t be deleted, but they will no longer be in { source_book.name }:
                </span>
                <ContentList contents={shared} />
              </>
            }
            <span>This cannot be undone.</span>
          </>
        }
        { failed.length > 0 &&
          <span style={{ color: "red" }}>
            Couldn&apos;t update or delete: { failed.join(", ") }. The source book wasn&apos;t deleted, so you can try again.
          </span>
        }
        { progress && <span>Working... { progress.done } / { progress.total }</span> }
        <FlexBox direction="row">
          <FlexBox xs_expand={0}>
            <DandDButtonBox color="red"
              name={only_in.length > 0 ? `Delete ${source_book.name} and ${only_in.length} Things` : `Delete ${source_book.name}`}
              disabled={contents === null || running || !api.online}
              onClick={deleteIt}
            />
          </FlexBox>
          <FlexBox xs_expand={0}>
            <DandDButtonBox name="Cancel" disabled={running} onClick={onClose} />
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </ModalOrDrawer>
  );
}
